import { useCallback, useEffect, useMemo, useRef } from 'react';

import { bind, type UnbindFn } from 'bind-event-listener';

const PRELOAD_MAX_AGE = 5 * 60 * 1000; // 5 mins
const TIME_TO_INTENT = 200;

export type OnLoad<T> = (obj: { dispose: () => void; reference: T }) => void;

export type UsePressablePreloadRefProps<T> = {
	load: () => T;
	loadOnFocus?: boolean;
	onBeforeLoad?: () => void;
	onLoad: OnLoad<T>;
	preloadOnFocus?: boolean;
};

export function usePressablePreloadRef<T extends { dispose: () => void }>({
	load,
	onLoad,
	onBeforeLoad,
	loadOnFocus = false,
	preloadOnFocus = false,
}: UsePressablePreloadRefProps<T>): (element: HTMLElement | null) => void {
	const nodeRef = useRef<HTMLElement | null>(null);
	const eventListeners = useRef<UnbindFn[]>([]);

	const { clickListener, mouseDownListener, preloadReference, cancelPreload } = useMemo(() => {
		const request: {
			load?: { dispose: () => void; reference: T };
			preload?: { dispose: () => void; reference: T };
			preloadIntentTimeout?: ReturnType<typeof setTimeout>;
			preloadMaxAgeTimeout?: ReturnType<typeof setTimeout>;
		} = {};

		const clearPreloadTimeouts = () => {
			if (typeof request.preloadIntentTimeout === 'number') {
				clearTimeout(request.preloadIntentTimeout);
				delete request.preloadIntentTimeout;
			}

			if (typeof request.preloadMaxAgeTimeout === 'number') {
				clearTimeout(request.preloadMaxAgeTimeout);
				delete request.preloadMaxAgeTimeout;
			}
		};

		const cancelPreload = () => {
			clearPreloadTimeouts();
			request.preload?.dispose();
		};

		const loadReference = () => {
			if (request.load) {
				return request.load;
			}

			const reference = request.preload?.reference ?? load();
			if (request.preload) {
				delete request.preload;
			}

			request.load = {
				reference,
				dispose() {
					reference.dispose();
					delete request.load;
				},
			};

			return request.load;
		};

		const clickListener = () => {
			clearPreloadTimeouts();
			if (onBeforeLoad) {
				onBeforeLoad();
			}
			onLoad(loadReference());
		};

		const mouseDownListener = () => {
			clearPreloadTimeouts();
			loadReference();
		};

		const preloadReference = () => {
			if (request.preloadIntentTimeout || request.preload) {
				return;
			}

			request.preloadIntentTimeout = setTimeout(() => {
				delete request.preloadIntentTimeout;

				const reference = load();

				request.preload = {
					reference,
					dispose() {
						reference.dispose();
						delete request.preload;
					},
				};

				request.preloadMaxAgeTimeout = setTimeout(() => {
					delete request.preloadMaxAgeTimeout;
					request.preload?.dispose();
				}, PRELOAD_MAX_AGE);
			}, TIME_TO_INTENT);
		};

		return {
			cancelPreload,
			clickListener,
			mouseDownListener,
			preloadReference,
		};
	}, [load, onBeforeLoad, onLoad]);

	const cleanUpEventListeners = useCallback(() => {
		eventListeners.current.forEach((unbind) => {
			unbind();
		});
		eventListeners.current = [];
	}, []);

	const addEventListener = useCallback(
		(element: HTMLElement, type: string, callback: EventListener) => {
			const unbind = bind(element, {
				type,
				listener: callback,
			});

			eventListeners.current.push(unbind);
		},
		[],
	);

	const refCallback = useCallback(
		(element: HTMLElement | null) => {
			if (element !== nodeRef.current) {
				if (nodeRef.current) {
					cancelPreload();
					cleanUpEventListeners();
				}
				if (element) {
					addEventListener(element, 'click', clickListener);
					addEventListener(element, 'mousedown', mouseDownListener);
					addEventListener(element, 'mouseenter', preloadReference);
					addEventListener(element, 'mouseleave', cancelPreload);
					if (loadOnFocus) {
						addEventListener(element, 'focusin', clickListener);
					} else if (preloadOnFocus) {
						addEventListener(element, 'focusin', preloadReference);
						addEventListener(element, 'focusout', cancelPreload);
					}
				}
				// Update the ref with the new element
				nodeRef.current = element;
			}
		},
		[
			clickListener,
			mouseDownListener,
			preloadReference,
			cancelPreload,
			loadOnFocus,
			addEventListener,
			cleanUpEventListeners,
			preloadOnFocus,
		],
	);

	useEffect(() => {
		return () => {
			cleanUpEventListeners();
		};
	}, [cleanUpEventListeners]);

	return refCallback;
}
